import { Formik, FormikProps, Form } from 'formik';
import React, { FunctionComponent } from 'react';
import Button from '../../Button/Button';
import FormField from '../../FormElements/FormField/FormikFormField';
import SettingsModal from '../../SettingsModal/SettingsModal';
import Loader from '@justgiving/loading/es/Loading';
import { AuctionLot } from '../../../../types/__generated__';
import InlineFormContainer from '../../FormElements/InlineFormContainer/InlineFormContainer';
import styles from './AuctionBiddingModal.scss';
import useBidOnAuctionLot from '../../../hooks/useBidOnAuctionLot';
import { useApp } from '../../../hooks/useApp';

export interface WinningBid {
  lotId: string;
  bidAmount: number;
}

interface AuctionBiddingModalProps {
  lot: AuctionLot;
  onClose: () => void;
  updateUserWinningBids: (bid: WinningBid, isUser: boolean) => void;
}

export const AuctionBiddingModal: FunctionComponent<AuctionBiddingModalProps> = ({
  lot,
  onClose,
  updateUserWinningBids,
}) => {
  const [placeBid, mutationResult] = useBidOnAuctionLot();
  const { showToast } = useApp();
  // console.log(mutationResult.data);
  // console.log(lot);

  type AuctionBidFormValues = {
    bidAmount?: number;
    emailAddress?: string;
  };

  type AuctionBidFormErrors = Partial<
    Record<keyof AuctionBidFormValues, string>
  >;

  let initialValues: AuctionBidFormValues = {
    bidAmount: lot.highestBid.value + 1,
    emailAddress: undefined,
  };

  const handleValidate = (
    values: AuctionBidFormValues,
  ): AuctionBidFormErrors => {
    // console.log('validating bid');
    let errors: AuctionBidFormErrors = {};

    if (values.bidAmount && values.bidAmount <= lot.highestBid.value) {
      errors.bidAmount = `Must be at least £${lot.highestBid.value + 1}`;
    }

    if (!values.bidAmount) {
      errors.bidAmount = `Please enter a bid amount greater than £${lot
        .highestBid.value + 1}`;
    }

    return errors;
  };

  const handleSubmit = async (values: AuctionBidFormValues) => {
    const bidResponse = await placeBid({
      slug: '12345',
      lotId: lot.legacyId,
      bidAmount: {
        value: values.bidAmount || 0,
        currencyCode: 'GBP',
      },
      emailAddress: values.emailAddress || '',
    });

    if (bidResponse.data?.bidOnAuctionLot?.successful) {
      updateUserWinningBids(
        {
          lotId: bidResponse.data.bidOnAuctionLot.lotId,
          bidAmount: bidResponse.data.bidOnAuctionLot.highestBid.value,
        },
        true,
      );
    } else {
      const higherBidAmount =
        bidResponse.data?.bidOnAuctionLot?.highestBid.value;
      if (!higherBidAmount) {
        showToast({
          message: 'Sorry, your bid could not be placed - please try again',
          type: 'warning',
          dismissAfter: 10000,
        });
      }
    }
  };

  return (
    <SettingsModal
      title={`Place your bid for: ${lot.title}`}
      onCloseRequest={onClose}
    >
      <Formik
        onSubmit={handleSubmit}
        validate={handleValidate}
        initialValues={initialValues}
        validateOnChange={true}
      >
        {({
          isValid,
          isSubmitting,
          values,
          touched,
          errors,
          setFieldValue,
          setFieldTouched,
        }: FormikProps<AuctionBidFormValues> & {
          errors: AuctionBidFormErrors;
        }) => {
          if (
            !touched.bidAmount &&
            !errors.bidAmount &&
            values.bidAmount &&
            values.bidAmount <= lot.highestBid.value
          ) {
            setFieldTouched('bidAmount');
            setFieldValue('bidAmount', values.bidAmount.toString());
          }

          return (
            <>
              <div style={{ textAlign: 'center', paddingBottom: '24px' }}>
                <img src={lot.image.url} alt={lot.title} width={'50%'} />
              </div>
              {mutationResult.data?.bidOnAuctionLot?.successful ? (
                <div>
                  <h2>Congratulations! You're the highest bidder!</h2>
                  <Button type="button" onClick={onClose}>
                    Close
                  </Button>
                </div>
              ) : (
                <>
                  {mutationResult.data?.bidOnAuctionLot?.successful === false &&
                  values.bidAmount &&
                  values.bidAmount <=
                    mutationResult.data?.bidOnAuctionLot.highestBid.value ? (
                    <>
                      <div>
                        <h2 className={styles.error_heading}>
                          Someone has just bid £
                          {
                            mutationResult.data?.bidOnAuctionLot.highestBid
                              .value
                          }{' '}
                          - please bid higher!
                        </h2>
                      </div>
                    </>
                  ) : (
                    <h3>Current highest bid is: £{lot.highestBid.value}</h3>
                  )}
                  <p>{lot.description}</p>
                  <Form>
                    <InlineFormContainer
                      touched={touched.bidAmount}
                      error={errors.bidAmount}
                    >
                      <FormField
                        id="bidAmount"
                        name="bidAmount"
                        label={'Bid Amount (£)'}
                        isRequired={true}
                        displayError={false}
                        className={styles.form_field}
                      />
                    </InlineFormContainer>
                    <InlineFormContainer
                      touched={touched.emailAddress}
                      error={errors.emailAddress}
                    >
                      <FormField
                        id="emailAddress"
                        name="emailAddress"
                        label={'Your Email Address'}
                        isRequired={true}
                        displayError={false}
                        className={styles.form_field}
                      />
                    </InlineFormContainer>

                    <Button
                      disabled={isSubmitting || !isValid}
                      type="submit"
                      className="jg-space-mbsm"
                    >
                      {isSubmitting ? (
                        <Loader size="small" colour="white" />
                      ) : (
                        'Place your bid!'
                      )}
                    </Button>
                    {lot.lotValue &&
                      values.bidAmount &&
                      lot.lotValue.value < values.bidAmount && (
                        <>
                          <p>
                            <i>
                              <strong>Note: </strong>This lot is valued at{' '}
                              <strong>£{lot.lotValue.value}</strong>.
                            </i>
                          </p>
                          <p>
                            <i>
                              By bidding <strong>£{values.bidAmount}</strong>,
                              you are making a purchase of{' '}
                              <strong>£{lot.lotValue.value} </strong>and an
                              additional donation of{' '}
                              <strong>
                                £{values.bidAmount - lot.lotValue.value}
                              </strong>
                              .
                            </i>
                          </p>
                          <p>
                            <i>
                              The donation of{' '}
                              <strong>
                                £{values.bidAmount - lot.lotValue.value}
                              </strong>{' '}
                              is eligible for Gift Aid.
                            </i>
                          </p>
                        </>
                      )}
                    <Button
                      type="button"
                      onClick={onClose}
                      disabled={isSubmitting || mutationResult.loading}
                    >
                      Cancel
                    </Button>
                  </Form>
                </>
              )}
            </>
          );
        }}
      </Formik>
    </SettingsModal>
  );
};

export default AuctionBiddingModal;
